import { LitElement, css, html } from 'lit';
import { customElement, state } from 'lit/decorators';

import { FeatureType } from '../../../types';
import { RangeSliderComponent } from '../../range-slider-component';
import { StateController } from '../../../helpers/state/state-management/state-controller';
import accountInfo from '../../../helpers/state/account-info';
import featuresInfo from '../../../helpers/state/features-info';
import { getVisibleTextColor } from '../../../helpers/colors';
import { palette } from '../../../style/variables';
import topicData from '../../../helpers/state/topic-data';

@customElement('header-logo')
export class HeaderLogo extends LitElement {
    amplitude_callsite: string = 'Header Logo';

	private readonly bindTopicState = new StateController(this, topicData);
    private readonly bindAccountState = new StateController(this, accountInfo);

    @state()
    changeOpen: boolean = false;

	async onChangeHeaderUrl(e: Event) {
		const headerUrl = (e.target as HTMLInputElement).value;

        await topicData.fetchAndUpdateTopicData({
            header_url: headerUrl
        }, 'header_url');
	}

    private async onChangeMobileHeaderSize(e: Event) {
        const mobileHeaderSize = (e.target as RangeSliderComponent).value;

        await topicData.fetchAndUpdateTopicData({
            mobile_header_size: mobileHeaderSize
        }, 'mobile_header_size');
    }

    openUpgradeDrawerLogo() {
        this.dispatchEvent(
            new CustomEvent('open-upgrade-dialog', {
                detail: {
                    featureType: FeatureType.HeaderLogo
                },
            })
        );
    }

    openUpgradeDrawerLinkOut() {
        this.dispatchEvent(
            new CustomEvent('open-upgrade-dialog', {
                detail: {
                    featureType: FeatureType.HeaderLinkOut
                },
            })
        );
    }

    async onChangeHeaderLogoEvent(e: CustomEvent) {
        const headerLogo = e.detail?.imageUrl ?? '';

        await topicData.fetchAndUpdateTopicData({
            header_logo: headerLogo
        }, 'header_logo');

        this.changeOpen = false;
    }

    toggleEditMode() {
        this.changeOpen = !this.changeOpen;
    }

    render() {
        const headerColor = topicData.backgroundColor;
        const logoFile = (getVisibleTextColor(headerColor) === palette.primaryBlue) ? 'logo_black.png' : 'logo_white.png';
        const logo = topicData.topic?.header_logo === '/images/logo.png' || !featuresInfo.headerLogoEnabled ? `/images/${logoFile}` : topicData.topic?.header_logo ?? `/images/${logoFile}`;
        return html`
            <upgrade-div .isLocked=${!featuresInfo.headerLogoEnabled} .feature=${FeatureType.HeaderLogo}>
                <div class="header-logo-container">
                    <h4 class="header-logo-label">Logo
                        ${this.changeOpen ?
                            html`<form-button variant="outline" size="xs"  @click="${this.toggleEditMode}">Cancel</form-button>`
                        :
                            html`<form-button variant="outline" size="xs" @click="${this.toggleEditMode}">Change</form-button>`}
                    </h4>
                    ${this.changeOpen ? html`
                        <image-upload-component
                            .imageUrl="${topicData.topic?.header_logo ?? ''}"
                            .autoUpload="${true}"
                            @retreived-autoupload-url="${this.onChangeHeaderLogoEvent}"
                            @changed-image-url="${this.onChangeHeaderLogoEvent}"
                        ></image-upload-component>
                    ` : html` <div class="logo-container"><img src="${logo ?? ''}" alt="Current Logo" style="background-color: ${topicData.backgroundColor ?? 'white'}; border-radius: 5px; padding: 5px; max-width: 30%;"/></div>`}
                    <proxi-textfield
                        outlined
                        id="headerURL"
                        class="input"
                        label="Header Redirect URL"
                        @change=${this.onChangeHeaderUrl}
                        value="${topicData.topic?.header_url ?? ''}"
                        helper="URL to open when logo is  clicked"
                        charcounter
                        maxlength="2000">
                    </proxi-textfield>

                    <h4 class="header-logo-label">Header Size</h4>
                    <p class="size-hint">Adjusts the logo and header size.</p>
                    <range-slider-component
                        id="mobileHeaderSizeSlider"
                        minString="Small"
                        maxString="Large"
                        min=${24}
                        max=${80}
                        step=${4}
                        unit=${'px'}
                        .value=${topicData.topic?.mobile_header_size ?? 32}
                        @change=${this.onChangeMobileHeaderSize}
                    ></range-slider-component>
                </div>
            </upgrade-div>
        `;
    }

    static get styles() {
        return css`
		    h4 {
                display: flex;
                align-items: center;
            }

            .input {
                width: 100%;
                padding: auto 5px;
            }

            .gatedField {
                display: flex;
                flex-direction: column;
                cursor: pointer;
            }

            .menuPng {
                margin-left: 0.25rem;
                height: 20px;
            }

            #headerURL {
                margin-top: 10px;
            }

            .header-logo-label {
                margin: 0;
                display: flex;
                align-items: center;
                gap: 10px;
                justify-content: flex-start;
                flex-direction: row;
            }

            .logo-container {
                display: flex;
                flex-direction: row;
                align-items: center;
                gap: 10px;
            }

            .header-logo-label {
                margin-bottom: 10px;
            }

            .header-logo-container {
                display: flex;
                flex-direction: column;
                align-items: stretch;
                padding: 10px 0;
            }

            .size-hint {
                font-size: 0.75rem;
                color: var(--theme-lighter-text-color);
                margin: 0 0 6px;
            }

            .header-logo-container img {
                max-width: 30%;
                margin-bottom: 10px;
            }
            .header-logo-container button {
                margin-top: 10px;
                padding: 5px 10px;
                background-color: #0078d4;
                color: white;
                border: none;
                border-radius: 4px;
                cursor: pointer;
            }
            .header-logo-container button:hover {
                background-color: #005a9e;
            }
            .header-logo-container mwc-textfield {
                margin-top: 10px;
                width: 100%;
            }
        `;
    }
}
